import * as React from "react"
import { Check } from "lucide-react"

import { TAP_TARGET } from "@/lib/ui-tokens"
import { cn } from "@/lib/utils"
import { Button } from "@/shadcn/components/ui/button"
import { Separator } from "@/shadcn/components/ui/separator"
import { type AutomationStep } from "./types"

/**
 * The wizard chrome every step shares: page header, 3-step stepper, divider,
 * body, wizard actions.
 *
 * Every frame of the Figma page draws this same shell with a different body, so
 * the flow mounts one `<AutomationShell step>` per route and passes the step
 * component as children.
 *
 * The actions are inert until handed a handler — `onBack`/`onPrimary` absent
 * renders the buttons disabled-looking but clickable-dead, which is what the
 * design harness wants and what a step still being wired can live with.
 */

const STEP_SUBTITLE: Record<AutomationStep, string> = {
  source: "Choose the source we'll pull new content from.",
  find: "Choose the source we'll pull new content from.",
  // Deliberately generic. A caller that knows the source names it — YouTube's
  // "Choose which videos we'll post." was the default here, which captioned a
  // Facebook Page and an Instagram account with it too.
  configure: "Choose what we'll post from this source.",
  "feed-preview": "Choose the source we'll pull new content from.",
  "post-preview": "Here's how your posts will look.",
  building: "",
  destinations: "Choose where we'll post your content.",
  review: "Review the details and create your automation.",
}

/**
 * Which stepper node the body belongs to.
 *
 * `building` and `post-preview` are both node 1: they describe the source the
 * user just settled on — the checklist inspects it, the post card previews what
 * it will publish — and both run before any destination is picked.
 */
const STEP_INDEX: Record<AutomationStep, 1 | 2 | 3> = {
  source: 1,
  find: 1,
  configure: 1,
  "feed-preview": 1,
  "post-preview": 1,
  building: 1,
  destinations: 2,
  review: 3,
}

function StepperNode({
  index,
  label,
  caption,
  state,
  onSelect,
}: {
  index: number
  label: string
  caption: string
  state: "done" | "current" | "todo"
  /** Omit to render the node inert, which is what the design harness wants. */
  onSelect?: () => void
}) {
  const active = state !== "todo"
  const clickable = Boolean(onSelect)
  const body = (
    <>
      <div
        className={cn(
          "tw:flex tw:size-8 tw:items-center tw:justify-center tw:rounded-full tw:text-sm tw:font-semibold",
          active
            ? "tw:bg-primary tw:text-primary-foreground"
            : cn(
                "tw:border-[1.5px] tw:border-border tw:text-muted-foreground",
                // The circle carries the affordance: a step you haven't reached
                // fills its outline in on hover, so the stepper reads as
                // navigable without anything moving.
                clickable &&
                  "tw:transition-colors tw:group-hover:border-primary tw:group-hover:text-foreground"
              )
        )}
      >
        {state === "done" ? <Check className="tw:size-4" /> : index}
      </div>
      <div className="tw:flex tw:flex-col tw:items-center tw:gap-0.5 tw:text-center">
        <span
          className={cn(
            "tw:text-sm tw:font-medium tw:whitespace-nowrap",
            state === "todo"
              ? cn(
                  "tw:text-muted-foreground",
                  clickable && "tw:transition-colors tw:group-hover:text-foreground"
                )
              : "tw:text-foreground"
          )}
        >
          {label}
        </span>
        <span className="tw:text-xs tw:whitespace-nowrap tw:text-muted-foreground">
          {caption}
        </span>
      </div>
    </>
  )

  const frame = "tw:flex tw:shrink-0 tw:flex-col tw:items-center tw:gap-2.5"

  if (!onSelect) {
    return <div className={frame}>{body}</div>
  }

  /*
   * A real <button>, not a div with an onClick: the stepper this replaces was
   * three divs wearing role="button", so a keyboard user could tab onto a step
   * and get nothing back from Enter or Space.
   */
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={state === "current" ? "step" : undefined}
      className={cn(
        frame,
        "tw:group tw:cursor-pointer tw:rounded-md tw:outline-none tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50"
      )}
    >
      {body}
    </button>
  )
}

export function WizardStepper({
  current,
  onSelect,
}: {
  current: 1 | 2 | 3
  /**
   * Omit and the stepper is inert, as the design draws it. Given a handler every
   * node becomes a button — forwards as well as back.
   *
   * Whether a forward jump is actually allowed is deliberately NOT decided here:
   * only the caller holds the route state that answers it, and the legacy
   * stepper's refusal was a toast rather than a dead control, which needs the
   * click to arrive to be able to explain itself.
   */
  onSelect?: (index: 1 | 2 | 3) => void
}) {
  const nodes = [
    { label: "Source", caption: "choose input" },
    { label: "Destinations", caption: "choose outputs" },
    { label: "Review", caption: "review & start" },
  ]
  return (
    <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-[600px] tw:items-start tw:justify-center tw:gap-2">
      {nodes.map((node, i) => {
        const index = (i + 1) as 1 | 2 | 3
        const state =
          index < current ? "done" : index === current ? "current" : "todo"
        return (
          <div key={node.label} className="tw:contents">
            {i > 0 ? (
              <div className="tw:flex tw:h-8 tw:min-w-0 tw:flex-1 tw:items-center">
                <div
                  className={cn(
                    "tw:h-0.5 tw:w-full",
                    index <= current ? "tw:bg-primary" : "tw:bg-border"
                  )}
                />
              </div>
            ) : null}
            <StepperNode
              index={index}
              label={node.label}
              caption={node.caption}
              state={state}
              onSelect={onSelect ? () => onSelect(index) : undefined}
            />
          </div>
        )
      })}
    </div>
  )
}

export function WizardActions({
  backLabel,
  primaryLabel,
  primaryDisabled,
  onBack,
  onPrimary,
}: {
  backLabel?: string
  primaryLabel?: string
  primaryDisabled?: boolean
  onBack?: () => void
  onPrimary?: () => void
}) {
  if (!backLabel && !primaryLabel) return null
  return (
    <div className="tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-3">
      {backLabel ? (
        <Button variant="outline" className={TAP_TARGET} onClick={onBack}>
          {backLabel}
        </Button>
      ) : (
        <span />
      )}
      {primaryLabel ? (
        <Button
          className={cn(TAP_TARGET, primaryDisabled && "tw:opacity-50")}
          aria-disabled={primaryDisabled || undefined}
          // aria-disabled rather than `disabled` is the design's choice, so the
          // guard has to live here too or a disabled primary would still fire.
          onClick={primaryDisabled ? undefined : onPrimary}
        >
          {primaryLabel}
        </Button>
      ) : null}
    </div>
  )
}

export function AutomationShell({
  step,
  title = "Create Automation",
  subtitle,
  backLabel,
  primaryLabel,
  primaryDisabled = false,
  onBack,
  onPrimary,
  onStepSelect,
  className,
  children,
}: {
  step: AutomationStep
  title?: string
  /** Muted line under the title. Defaults per step when omitted. */
  subtitle?: string
  backLabel?: string
  primaryLabel?: string
  primaryDisabled?: boolean
  onBack?: () => void
  onPrimary?: () => void
  /** Makes the stepper navigable. See WizardStepper.onSelect. */
  onStepSelect?: (step: 1 | 2 | 3) => void
  className?: string
  children?: React.ReactNode
}) {
  const copy = subtitle ?? STEP_SUBTITLE[step]

  return (
    <div
      className={cn(
        // max-w matches the Figma frame (1040 = 960 content + 2×40 padding).
        // Without it the page stretches on wide viewports and every fixed-size
        // element reads small against an over-wide container.
        // Vertical rhythm steps down at mobile: Figma's 40px gaps and 40px
        // padding are drawn for desktop, and unchanged they spend ~330px of a
        // 667px phone screen on chrome before the first card.
        // Children stretch rather than centering: `items-center` made every
        // flex child shrink-wrap, so a step body was only full width if it
        // said so itself — and a caller that wraps the step in a plain div of
        // its own (CreateFeed does) reintroduced the shrink-wrap one level
        // down, leaving the step's `w-full` to resolve against a
        // content-derived width. FindStep's search card was then wide when
        // feed rows sat below it and compacted to the empty state's width
        // without them. The header and stepper center themselves instead.
        "tw:mx-auto tw:flex tw:w-full tw:max-w-[1040px] tw:flex-col tw:gap-6 tw:bg-card tw:px-5 tw:py-6 tw:text-card-foreground tw:md:gap-10 tw:md:px-10 tw:md:py-14",
        className
      )}
    >
      <div className="tw:flex tw:flex-col tw:items-center tw:gap-1.5 tw:text-center">
        <h1 className="tw:font-heading tw:text-2xl tw:font-semibold tw:text-foreground">
          {title}
        </h1>
        {/* The Building screen moves its own heading + copy under the Zap badge. */}
        {copy && step !== "building" ? (
          <p className="tw:max-w-[314px] tw:text-sm tw:font-medium tw:text-balance tw:text-muted-foreground">
            {copy}
          </p>
        ) : null}
      </div>

      <WizardStepper current={STEP_INDEX[step]} onSelect={onStepSelect} />

      <Separator />

      {children}

      <WizardActions
        backLabel={backLabel}
        primaryLabel={primaryLabel}
        primaryDisabled={primaryDisabled}
        onBack={onBack}
        onPrimary={onPrimary}
      />
    </div>
  )
}
